import { StrictMode, useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { PrivateRoute } from './components/PrivateRoute';
import { LoadingScreen } from './components/LoadingScreen';
import { useAuthStore } from './store/authStore';
import { useAuth } from './hooks/useAuth';
import { CookieConsentBanner } from './components/CookieConsent';

const AuthPage = lazy(() => import('./pages/AuthPage').then(m => ({ default: m.AuthPage })));
const Register = lazy(() => import('./pages/Register'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Messages = lazy(() => import('./pages/Messages').then(m => ({ default: m.Messages })));
const Documents = lazy(() => import('./pages/Documents').then(m => ({ default: m.Documents })));
const Forum = lazy(() => import('./pages/Forum').then(m => ({ default: m.Forum })));
const MyPosts = lazy(() => import('./pages/MyPosts').then(m => ({ default: m.MyPosts })));
const Council = lazy(() => import('./pages/Council'));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const Admin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })));
const Assistance = lazy(() => import('./pages/Assistance').then(m => ({ default: m.Assistance })));
const LegalHub = lazy(() => import('./pages/LegalHub').then(m => ({ default: m.LegalHub })));
const LegalMentions = lazy(() => import('./pages/legal/LegalMentions').then(m => ({ default: m.LegalMentions })));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService').then(m => ({ default: m.TermsOfService })));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const ForumRules = lazy(() => import('./pages/legal/ForumRules').then(m => ({ default: m.ForumRules })));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy').then(m => ({ default: m.CookiePolicy })));
const CouncilDiscussion = lazy(() => import('./pages/CouncilDiscussion').then(m => ({ default: m.CouncilDiscussion })));
const CouncilDrive = lazy(() => import('./pages/CouncilDrive').then(m => ({ default: m.CouncilDrive })));
const CouncilChat = lazy(() => import('./pages/CouncilChat').then(m => ({ default: m.CouncilChat })));

export function App() {
  const { isLoading } = useAuthStore();
  const [appReady, setAppReady] = useState(false);

  useAuth(); // Keep this to handle auth state changes

  useEffect(() => {
    // Set a timeout to show content even if loading takes too long
    const timer = setTimeout(() => {
      if (isLoading) {
        console.log('Forcing app to load after timeout');
        setAppReady(true);
      }
    }, 10000); // 10 seconds max wait

    if (!isLoading) {
      setAppReady(true);
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  if (isLoading && !appReady) {
    return <LoadingScreen />;
  }

  return (
    <StrictMode>
      {/* Cookie Consent Banner */}
      <CookieConsentBanner />

      <Suspense fallback={<LoadingScreen />}>
        <Routes>
        {/* Public Routes */}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/register" element={<Register />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />

        {/* Council Drive & Chat - Public access with password */}
        <Route path="/council-drive" element={<CouncilDrive />} />
        <Route path="/council-chat" element={<CouncilChat />} />
        <Route path="/drive" element={<CouncilDrive />} />

        {/* Legal Routes */}
        <Route path="/legal" element={<LegalHub />} />
        
        {/* Protected Routes */}
        <Route element={<Layout />}>
          <Route path="/" element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          } />
          <Route path="/forum" element={
            <PrivateRoute>
              <Forum />
            </PrivateRoute>
          } />
          <Route path="/my-posts" element={
            <PrivateRoute>
              <MyPosts />
            </PrivateRoute>
          } />
          <Route path="/messages" element={
            <PrivateRoute>
              <Messages />
            </PrivateRoute>
          } />
          <Route path="/documents" element={
            <PrivateRoute>
              <Documents />
            </PrivateRoute>
          } />
          <Route path="/council" element={
            <PrivateRoute>
              <Council />
            </PrivateRoute>
          } />
          <Route path="/council-discussion" element={
            <PrivateRoute>
              <CouncilDiscussion />
            </PrivateRoute>
          } />
          <Route path="/assistance" element={
            <PrivateRoute>
              <Assistance />
            </PrivateRoute>
          } />
          <Route path="/profile" element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          } />
          <Route path="/admin/*" element={
            <PrivateRoute>
              <Admin />
            </PrivateRoute>
          } />
          
          {/* Legal Routes */}
          <Route path="/legal/mentions" element={<LegalMentions />} />
          <Route path="/legal/cgu" element={<TermsOfService />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/forum-rules" element={<ForumRules />} />
          <Route path="/legal/cookies" element={<CookiePolicy />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </StrictMode>
  );
}